"use client"

import { useState } from "react"
import { signIn } from "next-auth/react"
import { Pencil, Layers, Box, MailCheck } from "lucide-react"
import { registerAction } from "@/lib/actions/register.action"
import { Logo } from "@/components/Logo"

export default function RegisterPage() {
  const [loading, setLoading] = useState(false)
  const [sent, setSent] = useState(false)
  const [sentEmail, setSentEmail] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({})

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setLoading(true)
    setError(null)
    setFieldErrors({})

    const form = new FormData(e.currentTarget)
    const email = form.get("email") as string
    const input = {
      firstName: form.get("firstName") as string,
      lastName: form.get("lastName") as string,
      email,
      acceptCgu: form.get("acceptCgu") === "on",
    }

    try {
      const result = await registerAction(input)

      if (!result.ok) {
        setError(result.error)
        setFieldErrors(result.fieldErrors ?? {})
        setLoading(false)
        return
      }

      setSentEmail(email)
      setSent(true)
    } catch {
      setError("Une erreur est survenue lors de l'envoi de l'email. Réessayez dans un instant.")
      setLoading(false)
    }
  }

  function FieldError({ name }: { name: string }) {
    const errors = fieldErrors[name]
    if (!errors?.length) return null
    return <p className="text-xs text-red-500 mt-1">{errors[0]}</p>
  }

  return (
    <div className="min-h-screen flex">
      {/* Left panel — branded (identique au login) */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#1a1a2e] flex-col justify-between p-12 relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-white/3" />
          <div className="absolute -bottom-32 -left-16 w-80 h-80 rounded-full bg-white/3" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 rounded-full bg-[#e94560]/10" />
        </div>

        <div className="relative">
          <div className="flex items-center gap-3">
            <div>
              <Logo size="lg" onDark />
              <p className="mt-2 text-[10px] text-white/40 uppercase tracking-widest leading-none">Plans électriques</p>
            </div>
          </div>
        </div>

        <div className="relative space-y-8">
          <div>
            <h2 className="text-3xl font-bold text-white leading-tight">
              Concevez vos plans<br />électriques en 2D et 3D
            </h2>
            <p className="text-white/50 mt-3 text-sm leading-relaxed">
              Dessinez vos plans, placez vos symboles électriques,<br />et visualisez le résultat en 3D instantanément.
            </p>
          </div>

          <div className="space-y-4">
            {[
              { icon: Pencil, text: "Éditeur de plans 2D intuitif" },
              { icon: Layers, text: "Gestion multi-étages et escaliers" },
              { icon: Box, text: "Visualisation 3D temps réel" },
            ].map(({ icon: Icon, text }) => (
              <div key={text} className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                  <Icon className="w-4 h-4 text-white/70" />
                </div>
                <p className="text-sm text-white/70">{text}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="relative">
          <p className="text-xs text-white/30">&copy; 2025 archifacil — Plans électriques intelligents</p>
        </div>
      </div>

      {/* Right panel — form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-[#f5f5f7]">
        <div className="w-full max-w-sm">
          {/* Mobile logo */}
          <div className="flex items-center gap-3 mb-8 lg:hidden">
            <Logo size="lg" />
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
            {sent ? (
              <div className="text-center">
                <div className="w-12 h-12 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-4">
                  <MailCheck className="w-6 h-6 text-green-600" />
                </div>
                <h1 className="text-xl font-bold text-gray-900">Vérifiez votre boîte mail</h1>
                <p className="text-gray-500 text-sm mt-2 leading-relaxed">
                  Un email de confirmation a été envoyé à{" "}
                  <span className="font-medium text-gray-700">{sentEmail}</span>.
                  Cliquez sur le lien qu&apos;il contient pour définir votre mot de
                  passe et activer votre compte. Le lien est valable 24 heures.
                </p>
                <a
                  href="/login"
                  className="inline-block mt-6 text-sm text-[#e94560] hover:underline font-medium"
                >
                  Retour à la connexion
                </a>
              </div>
            ) : (
              <>
                <div className="mb-6">
                  <h1 className="text-xl font-bold text-gray-900">Créer un compte</h1>
                  <p className="text-gray-500 text-sm mt-1">Renseignez vos informations pour commencer</p>
                </div>

                {error && !Object.keys(fieldErrors).length && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
                    {error}
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                        Prénom *
                      </label>
                      <input
                        id="firstName"
                        name="firstName"
                        type="text"
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#e94560]/20 focus:border-[#e94560] transition-colors"
                        placeholder="Jean"
                      />
                      <FieldError name="firstName" />
                    </div>
                    <div>
                      <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                        Nom *
                      </label>
                      <input
                        id="lastName"
                        name="lastName"
                        type="text"
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#e94560]/20 focus:border-[#e94560] transition-colors"
                        placeholder="Dupont"
                      />
                      <FieldError name="lastName" />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                      Email *
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#e94560]/20 focus:border-[#e94560] transition-colors"
                      placeholder="jean@exemple.fr"
                    />
                    <FieldError name="email" />
                  </div>

                  <div>
                    <label className="flex items-start gap-2.5 cursor-pointer">
                      <input
                        type="checkbox"
                        name="acceptCgu"
                        required
                        className="mt-0.5 h-4 w-4 rounded border-gray-300 text-[#e94560] focus:ring-[#e94560]/30"
                      />
                      <span className="text-sm text-gray-600">
                        J&apos;accepte les{" "}
                        <a href="/cgu" target="_blank" className="text-[#e94560] hover:underline font-medium">
                          conditions générales d&apos;utilisation
                        </a>
                        {" "}(voir aussi la page{" "}
                        <a href="/confidentialite" target="_blank" className="text-[#e94560] hover:underline font-medium">
                          Données personnelles
                        </a>
                        )
                      </span>
                    </label>
                    <FieldError name="acceptCgu" />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-2.5 bg-[#e94560] text-white rounded-lg text-sm font-medium hover:bg-[#d63d56] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? "Envoi du lien..." : "Créer mon compte"}
                  </button>
                </form>

                <div className="relative my-5">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-200" />
                  </div>
                  <div className="relative flex justify-center text-xs">
                    <span className="bg-white px-3 text-gray-400">ou</span>
                  </div>
                </div>

                <button
                  onClick={() => signIn("google", { callbackUrl: "/" })}
                  className="w-full flex items-center justify-center gap-3 py-2.5 px-4 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium text-gray-700"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                  </svg>
                  S&apos;inscrire avec Google
                </button>
              </>
            )}
          </div>

          {!sent && (
            <div className="text-center mt-4">
              <p className="text-sm text-gray-600">
                Déjà un compte ?{" "}
                <a href="/login" className="text-[#e94560] hover:underline font-medium">
                  Se connecter
                </a>
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
